import { Plus, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useState } from 'react';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const { addToCart, cartItems } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const navigate = useNavigate();
  
  const isInCart = cartItems.some(item => item.product.id === product.id);
  const discount = product.original_price 
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : null;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    setIsAdding(true);
    addToCart(product);
    toast.success(`${product.name} added to cart`);
    setTimeout(() => setIsAdding(false), 1000);
  };

  const goToProduct = () => {
    navigate(`/products/${product.id}`);
  };

  return (
    <div 
      className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-[#F3F2EE] opacity-0 animate-fade-in-up cursor-pointer"
      data-testid={`product-card-${product.id}`}
      onClick={goToProduct}
    >
      {/* Image Container */}
      <div className="relative aspect-square rounded-xl overflow-hidden bg-[#F3F2EE] mb-4">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {discount && (
          <span className="absolute top-3 left-3 bg-[#1A1A1C] text-white text-xs font-medium px-3 py-1 rounded-full">
            -{discount}%
          </span>
        )}
        {product.is_bundle && (
          <span className="absolute top-3 right-3 bg-[#34C759] text-white text-xs font-medium px-3 py-1 rounded-full">
            Bundle
          </span>
        )}
      </div>

      {/* Content */}
      <div className="space-y-2">
        <span className="text-xs font-medium uppercase tracking-wide text-[#8A8A8E]">
          {product.category}
        </span>
        <h3 className="font-semibold text-[#1A1A1C] font-['Manrope'] line-clamp-2 leading-tight group-hover:underline">
          {product.name}
        </h3>
        <p className="text-sm text-[#5C5C60] line-clamp-2">
          {product.description}
        </p>
        
        {/* Price */}
        <div className="flex items-center gap-2 pt-2">
          <span className="text-lg font-bold text-[#1A1A1C]">
            ${product.price.toFixed(2)}
          </span>
          {product.original_price && (
            <span className="text-sm text-[#8A8A8E] line-through">
              ${product.original_price.toFixed(2)}
            </span>
          )}
        </div>

        {/* Add to Cart */}
        <button
          onClick={handleAddToCart}
          disabled={isAdding}
          className={`w-full mt-4 flex items-center justify-center gap-2 py-3 px-4 rounded-full font-medium transition-all duration-300 ${
            isInCart 
              ? 'bg-[#34C759] text-white' 
              : 'bg-[#1A1A1C] text-white hover:bg-[#333333] hover:-translate-y-0.5'
          }`}
          data-testid={`add-to-cart-${product.id}`}
        >
          {isAdding ? (
            <Check className="w-5 h-5" strokeWidth={1.5} />
          ) : (
            <>
              <Plus className="w-5 h-5" strokeWidth={1.5} />
              <span>{isInCart ? 'Add More' : 'Add to Cart'}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
